import React from 'react';
import styled from '@emotion/styled';
import type { Nullable } from 'corgi-types';
import { position } from 'styled-system';

import Badge from 'common/components/Badge/CountBadge';
import { IconButton } from 'common/components/Button/Icon';
import DefaultProductImage from 'common/components/DefaultProductImage';
import { Flex } from 'common/components/Flex';
import CardImage from 'common/components/Reclamation/CardImage';
import Icon from 'common/components/Reclamation/Icon';
import { HeadlineH8, SubtitleST4 } from 'common/components/Typography';
import { useLocalization } from 'common/providers/LocaleProvider';
import { focusOutlineCSSWithOffset } from 'common/theme/styles';
import { themeRight, themeTop } from 'common/theme/system-utilities';

export interface ProductDetailsProps {
  imagePath: Nullable<string>;
  title: string;
  subtitle: string;
  isDisabled: boolean;
  onItemDelete?: () => Promise<void> | undefined;
  claimedQuantity: number;
}

export const ITEM_QUANTITY_POSITION = '-8px';

const ImageWrapper = styled(Flex)<any>`
  ${position};
`;

const Quantity = styled(Badge)`
  ${position};
  ${themeTop};
  ${themeRight};
`;
const quantityStyles = {
  position: 'absolute',
  themeTop: ITEM_QUANTITY_POSITION,
  themeRight: ITEM_QUANTITY_POSITION,
};

const StyledIconButton = styled(IconButton)`
  ${focusOutlineCSSWithOffset};
`;

const ProductDetails = ({
  imagePath,
  title,
  subtitle,
  isDisabled,
  onItemDelete,
  claimedQuantity,
}: ProductDetailsProps) => {
  const { t } = useLocalization();

  return (
    <Flex
      as="header"
      alignItems="flex-start"
      data-testid="product-details"
      justifyContent="space-between"
    >
      <Flex alignItems="center">
        <ImageWrapper position="relative" mr="sp_8">
          {imagePath ? (
            <CardImage src={imagePath} alt={title} />
          ) : (
            <DefaultProductImage title={title} />
          )}
          {claimedQuantity && claimedQuantity > 1 && (
            <Quantity {...quantityStyles}>{claimedQuantity}</Quantity>
          )}
        </ImageWrapper>
        <Flex flexDirection="column">
          <HeadlineH8 data-testid="title">{title}</HeadlineH8>
          <SubtitleST4 data-testid="subtitle">{subtitle}</SubtitleST4>
        </Flex>
      </Flex>
      {!!onItemDelete && (
        <StyledIconButton
          themeWidth="sp_36"
          themeHeight="sp_36"
          data-testid="delete-button"
          aria-label={`${t('h24_delete')} ${title}`}
          disabled={isDisabled}
          onClick={onItemDelete}
        >
          <Icon name="close" />
        </StyledIconButton>
      )}
    </Flex>
  );
};

export default ProductDetails;
